import React, { useState, useEffect, Fragment } from "react";
import { useParams } from "react-router-dom";

import { gql, useMutation } from "@apollo/client";

import { useStripe, Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import styled from "styled-components";

import Subheadline from "../../components/Subheadline";
import AnimatedButton from "../../components/AnimatedButton";

import Icon from "../../utils/cardIcon";
import { centsToDollars } from "../../utils/currency";

import { paymentTypes } from "../../constants";

import { RightCol, Col, Title } from "../../styled";

const CENTS = 100;
const PERCENT = 100;

const Body = styled.p.attrs({
  className: "lh-copy measure-wide",
})``;

const Container = styled.div.attrs({
  className: "pa3 pa6-ns",
})`
  background-image: url("/img/clipboard.svg");
  z-index: -1;
  background-repeat: no-repeat;
  background-size: 40% 40%;
  background-position: 400px 100px;
`;

const BIND_POLICY_QUERY = gql`
  mutation BindInsurancePolicy($quoteId: String!, $iaSession: String!) {
    bindInsurancePolicy(quoteId: $quoteId, iaSession: $iaSession) {
      ok
      message
      policy {
        id
      }
    }
  }
`;

const CheckoutPaymentStatus = () => {
  let message = "Success! Your payment method has been saved.";
  return message;
};

const StripePaymentStatus = () => {
  const stripe = useStripe();
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    // Retrieve the "setup_intent_client_secret" query parameter appended to
    // your return_url by Stripe.js
    const clientSecret = new URLSearchParams(window.location.search).get(
      "setup_intent_client_secret"
    );

    // Retrieve the SetupIntent
    stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent }) => {
      // Inspect the SetupIntent `status` to indicate the status of the payment
      // to your customer.
      //
      // Some payment methods will [immediately succeed or fail][0] upon
      // confirmation, while others will first enter a `processing` state.
      //
      // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
      switch (setupIntent.status) {
        case "succeeded":
          setMessage("Success! Your payment method has been saved.");
          break;

        case "processing":
          setMessage(
            "Processing payment details. We'll update you when processing is complete."
          );
          break;

        case "requires_payment_method":
          // Redirect your user back to your payment page to attempt collecting
          // payment again
          setMessage(
            "Failed to process payment details. Please try another payment method."
          );
          break;
      }
    });
  }, [stripe]);

  return message;
};

export function Headline({ withholdPremium, withholdPremiumOrgOverride, jobWithholdPremium }) {
  const showCreditCardMessage = !withholdPremium && !withholdPremiumOrgOverride && !jobWithholdPremium;
  
  return (
    <Container>
      <Title>Final Review</Title>
      <Subheadline>
        <Body>
          We're ready to issue you your pay-as-you-go insurance policy. Clicking
          the "Complete Application" button will activate your insurance
          coverage which you can use to take on new job assignments. {showCreditCardMessage && "If you're paying via credit card we'll also charge the card you have on file."}
        </Body>
      </Subheadline>
    </Container>
  );
}

function Banner(props) {
  return (
    <div className="flex items-center justify-center pa1 pa4-l navy">
      <span className="lh-title ml3-l bg-lightest-blue ph2 ph4-l pv2 br2">
        {props.children}
      </span>
    </div>
  );
}

function getCoverageType(quote) {
  if (quote?.isGlAndWc === "true") {
    return (
      <>
        General Liability & <br />
        Workers Compensation
      </>
    );
  }
  if (quote?.isGlWcAndMedia === "true") {
    return (
      <>
        General Liability, <br />
        Workers Compensation<br />
        &  Media Coverage
      </>
    );
  }
  if (quote?.isGlAndMedia === "true") {
    return (
      <>
        General Liability & <br />
        Media Coverage
      </>
    );
  }
  if (quote?.isWcAndMedia === "true") {
    return (
      <>
        Media Coverage & <br />
        Workers Compensation
      </>
    );
  }
  if (quote?.isWcOnly === "true") {
    return "Workers Compensation";
  }
  if (quote?.isGlOnly === "true") {
    return "General Liability";
  }
  if (quote?.isMediaOnly === "true") {
    return "Media Coverage";
  }
}

function ConfirmationTable(props) {
  const { iaSession } = useParams();
  // Surfaced to the user when binding the policy fails, so "Complete
  // Application" never silently no-ops.
  const [bindError, setBindError] = useState(null);
  const [bindPolicy, { loading, reset }] = useMutation(BIND_POLICY_QUERY, {
    onCompleted: (resp) => {
      if (resp?.bindInsurancePolicy?.ok) {
        // bindInsurancePolicy is idempotent: an already-bound quote returns the
        // existing policy with ok:true (not an error), and an orphaned
        // complete-but-unbound session still binds here. So retries are always
        // safe. The refetched session carries the policy and renders
        // ApplicationComplete.
        setBindError(null);
        reset();
        return;
      }
      // ok:false (not a GraphQL error): the bind couldn't be captured—most
      // likely a stale card authorization that has since expired (auths lapse
      // after ~7 days), so the capture failed. Surface the gateway reason
      // rather than silently resetting. The contractor can't self-complete in
      // this state (it needs a fresh authorization), so they should contact
      // support.
      setBindError(
        resp?.bindInsurancePolicy?.message ||
          "We couldn't activate your coverage. Please contact support."
      );
      reset();
    },
    onError: (err) => {
      const gqlError = err.graphQLErrors?.[0];
      // bind has no completion lockout, so it never raises
      // APPLICATION_ALREADY_COMPLETED—surface whatever did go wrong instead of
      // letting the button silently no-op.
      setBindError(
        gqlError?.message ||
          "We couldn't complete your application. Please try again."
      );
    },
    refetchQueries: ["InsuranceApplicationSession"],
  });

  const {
    paymentProvider,
    withholdPremium,
    withholdPremiumOrgOverride,
    // True when the FE routed an orphaned (complete-but-unbound) session here to
    // let the contractor finish by binding the policy.
    isRemediation,
    data: {
      insuranceApplicationSession: {
        contractor: { unredactedDict, paymentMethod },
        job,
        quotes: { edges },
      },
    },
  } = props;

  const unredacted = JSON.parse(unredactedDict);
  const paymentMethodJSON = paymentMethod ? JSON.parse(paymentMethod) : null;

  // Only show payment section when ADD_CARD_DETAILS was in this session's steps.
  // For agency remit (withholdPremium at contractor, org, or job level), the backend skips ADD_CARD_DETAILS,
  // so the contractor didn't add a card in this session—any card shown would be from a prior application.
  const showPaymentSection = !withholdPremium && !withholdPremiumOrgOverride && !job?.withholdPremium;

  const firstQuote = edges?.[0]?.node;
  const quoteId = firstQuote?.id;
  const effectiveDate = firstQuote?.effectiveDate ? new Date(firstQuote.effectiveDate) : null;
  const includesGL = firstQuote 
    ? (firstQuote.isGlOnly === "true" || firstQuote.isGlAndWc === "true")
    : false;
  const wage = firstQuote?.job?.wage;

  let paymentStatus;

  switch (paymentProvider) {
    case paymentTypes.CHECKOUT:
      paymentStatus = <CheckoutPaymentStatus />;
      break;
    case paymentTypes.STRIPE:
      paymentStatus = <StripePaymentStatus />;
      break;
    default:
      paymentStatus = <></>;
  }

  return (
    <>
      {isRemediation && (
        <div className="pa3 mh3 mt3 br2 dark-blue bg-lightest-blue ba b--light-blue tc">
          You're almost done — confirm the details below and click "Complete
          Application" to activate your coverage.
        </div>
      )}
      {showPaymentSection && paymentMethodJSON && <Banner>{paymentStatus}</Banner>}
      <div className="pa1 pa4-l pt0">
        <div className="overflow-auto">
          <table className="f6 w-100 w-70-ns mw8 center-l" cellSpacing="0">
            <tbody className="lh-copy">
              <tr>
                <td className="pv2 pr3 gray">Name</td>
                <td className="pv2 pl3 pl0-ns pr3">
                  {unredacted.first_name} {unredacted.last_name}
                </td>
              </tr>
              <tr>
                <td className="pv2 pr3 gray v-top">Address</td>
                <td className="pv2 pl3 pl0-ns pr3-ns">
                  <div>{unredacted.address.line1}</div>
                  {unredacted.address.line2 ? (
                    <div>{unredacted.address.line2}</div>
                  ) : null}
                  {unredacted.address.line3 ? (
                    <div>{unredacted.address.line3}</div>
                  ) : null}
                  <div>
                    {unredacted.address.locality}, {unredacted.address.region}{" "}
                    {unredacted.address.postalcode}
                  </div>
                </td>
              </tr>
              {firstQuote?.job?.entity?.name && (
                <tr>
                  <td className="pv2 pr3 gray">Contracting entity</td>
                  <td className="pv2 pl3 pl0-ns pr3-ns">
                    {firstQuote.job.entity.name}
                  </td>
                </tr>
              )}
              {firstQuote?.job?.name && (
                <tr>
                  <td className="pv2 pr3 gray">Job Assignment</td>
                  <td className="pv2 pl3 pl0-ns pr3-ns">
                    {firstQuote.job.name}
                  </td>
                </tr>
              )}
              {getCoverageType(firstQuote) && (
                <tr>
                  <td className="pv2 pr3 gray">Coverage type</td>
                  <td className="pv2 pl3 pl0-ns pr3-ns">
                    {getCoverageType(firstQuote)}
                  </td>
                </tr>
              )}
              {effectiveDate && (
                <tr>
                  <td className="pv2 pr3 gray">Effective date</td>
                  <td className="pv2 pl3 pl0-ns pr3-ns">
                    {effectiveDate.toDateString()}
                  </td>
                </tr>
              )}
              {firstQuote?.netRate && (
                <tr>
                  <td className="pv2 pr3 gray">Net rate</td>
                  <td className="pv2 pl3 pl0-ns pr3-ns">
                    {centsToDollars(firstQuote.netRate)} per $100 earned
                  </td>
                </tr>
              )}
              {wage && firstQuote?.premiumAmountOwed && (
                <tr>
                  <td className="pv2 pr3 gray">
                    Premium owed (based on {centsToDollars(wage)} wage)
                    <br />
                    {includesGL && "+ taxes and fees"}
                  </td>
                  <td className="pv2 pl3 pl0-ns pr3-ns">
                    {centsToDollars(firstQuote.premiumAmountOwed)}
                  </td>
                </tr>
              )}
              {showPaymentSection && paymentMethodJSON ? (
                <tr>
                  <td className="pv2 pr3 gray">Payment method</td>
                  <td className="pv2 pl3 pl0-ns pr3">
                    {Icon(paymentMethodJSON.brand)} &nbsp;••••
                    {paymentMethodJSON.last4}
                  </td>
                </tr>
              ) : null}
              <tr>
                <th colSpan="2" className="dib mt4">
                  {bindError && (
                    <div
                      role="alert"
                      className="pa3 mb3 br2 dark-red bg-washed-red ba b--light-red normal"
                    >
                      {bindError}
                    </div>
                  )}
                  <AnimatedButton
                    isSubmitting={loading}
                    onClick={() => {
                      setBindError(null);
                      bindPolicy({
                        variables: { quoteId: quoteId, iaSession: iaSession },
                      });
                    }}
                    primary
                  >
                    Complete Application
                  </AnimatedButton>
                </th>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function FinalReviewWrapper(props) {
  const { paymentProvider, children } = props;

  if (paymentProvider === paymentTypes.STRIPE) {
    // Make sure to call `loadStripe` outside of a component's render to avoid
    // recreating the `Stripe` object on every render.
    const stripePromise = loadStripe(
      "pk_test_9Z12C94UZkJ8NRU3rSan6qnY00Lcaxwf17"
    );

    return <Elements stripe={stripePromise}>{children}</Elements>;
  } else {
    return <>{children}</>;
  }
}

export function FinalReview(props) {
  let provider = process.env.REACT_APP_PAYMENT_PROVIDER || "checkout";
  
  return (
    <FinalReviewWrapper paymentProvider={provider}>
      <ConfirmationTable {...props} paymentProvider={provider} />
    </FinalReviewWrapper>
  );
}
